import { useLocation } from "react-router-dom";
import { setPageLayout, clearPageLayout } from "../store/slice/page-layout";
import type { PageLayoutOptions } from "../store/slice/page-layout";
import { useLayoutEffect, useId, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { updatePageState } from "../store";
export interface PageConfig extends PageLayoutOptions {
    page_title: string;
    force_fullscreen?: boolean;
}
export function usePageWrapper(Component: React.FC, options: PageConfig) {
  return function WrappedComponent(props: any) {
    const dispatch = useDispatch();
    const { key: locationKey } = useLocation();
    const owner = useId();
    const currentTitle = useRef({
      page_title: options.page_title,
      force_fullscreen: options.force_fullscreen ?? false,
    });
    const updateTitle = useCallback((title: string, force_fullscreen: boolean) => {
      currentTitle.current = { page_title: title, force_fullscreen };
      dispatch(updatePageState(currentTitle.current));
    }, [dispatch]);
    useLayoutEffect(() => {
      dispatch(setPageLayout({ owner, locationKey,
        content_padding: options.content_padding,
        content_class_name: options.content_class_name,
        back_navigation: options.back_navigation,
      }));
      return () => { dispatch(clearPageLayout(owner)); };
    }, [dispatch, owner, locationKey, options.content_padding, options.content_class_name, options.back_navigation]);

    useLayoutEffect(() => {
      // Nested tabs keep this wrapper mounted. Preserve the resolved entity
      // name instead of replacing it with the generic route title on navigation.
      dispatch(updatePageState(currentTitle.current));

      // A destination page may never mount when its remote module is still
      // loading or failed to load. Release this page's title on navigation so
      // the header cannot keep presenting the previous page as the current one.
      return () => {
        dispatch(updatePageState({ page_title: null, force_fullscreen: false }));
      };
    }, [dispatch, locationKey, options.page_title, options.force_fullscreen]);

    return <Component updateTitle={updateTitle} {...props} />;
  };
}
